// src/disc/DiscComparisonTypeSelector.js
import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
} from '@mui/material';
import {
  People as PeopleIcon,
  PersonSearch as PersonSearchIcon,
  Groups as GroupsIcon,
} from '@mui/icons-material';
import { styled } from '@mui/system';
import { useTranslation } from 'react-i18next';

// Componente estilizado para las cards de tipo de análisis
const AnalysisTypeCard = styled(Card)(({ theme, selected }) => ({
  padding: theme.spacing(3),
  border: selected ? `3px solid ${theme.palette.primary.main}` : '2px solid #e0e0e0',
  backgroundColor: selected ? theme.palette.primary.light + '10' : 'white',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  '&:hover': {
    backgroundColor: selected ? theme.palette.primary.light + '20' : '#f5f5f5',
    transform: 'translateY(-4px)',
    boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
  },
}));

const IconWrapper = styled(Box)(({ theme, selected }) => ({
  width: 64,
  height: 64,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: selected ? theme.palette.primary.main : theme.palette.grey[200],
  color: selected ? 'white' : theme.palette.grey[600],
  marginBottom: theme.spacing(2),
  transition: 'all 0.3s ease',
}));

export default function DiscComparisonTypeSelector({
  selectedType,
  onTypeSelect,
}) {
  const { t } = useTranslation();

  const analysisTypes = [
    {
      id: 'compareCandidates',
      title: t('disc.discComparisons.compareCandidatesTitle'),
      description: t('disc.discComparisons.compareCandidatesDescription'),
      icon: <PeopleIcon sx={{ fontSize: 32 }} />,
    },
    {
      id: 'candidateVsBoss',
      title: t('disc.discComparisons.candidateVsBossTitle'),
      description: t('disc.discComparisons.candidateVsBossDescription'),
      icon: <PersonSearchIcon sx={{ fontSize: 32 }} />,
    },
    {
      id: 'teamAnalysis',
      title: t('disc.discComparisons.teamAnalysisTitle'),
      description: t('disc.discComparisons.teamAnalysisDescription'),
      icon: <GroupsIcon sx={{ fontSize: 32 }} />,
    },
  ];

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
          {t('disc.discComparisons.selectAnalysisType')}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {t('disc.discComparisons.subTitle')}
        </Typography>
      </Box>

      {/* Cards de tipos de análisis */}
      <Grid container spacing={3}>
        {analysisTypes.map((type) => (
          <Grid item xs={12} md={4} key={type.id}>
            <AnalysisTypeCard
              selected={selectedType === type.id}
              onClick={() => onTypeSelect(type.id)}
              elevation={selectedType === type.id ? 4 : 1}
            >
              <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                <IconWrapper selected={selectedType === type.id}>
                  {type.icon}
                </IconWrapper>
                
                <Typography 
                  variant="h6" 
                  gutterBottom 
                  sx={{ 
                    fontWeight: 'bold',
                    color: selectedType === type.id ? 'primary.main' : 'text.primary',
                  }}
                >
                  {type.title}
                </Typography>
                
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  sx={{ flexGrow: 1 }}
                >
                  {type.description}
                </Typography>
                
                {selectedType === type.id && (
                  <Box sx={{ mt: 2, textAlign: 'center' }}>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        color: 'primary.main',
                        fontWeight: 'bold',
                      }}
                    >
                      ✓ {t('selected')}
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </AnalysisTypeCard>
          </Grid>
        ))}
      </Grid>

      {/* Mensaje de ayuda */}
      {!selectedType && (
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {t('disc.discComparisons.selectAnalysisType')}
          </Typography>
        </Box>
      )}
    </Box>
  );
}